import { Show, createEffect, createMemo } from "solid-js";
import { Portal } from "solid-js/web";
import { useHashState } from "../../shared/routing/router";
import { useModalDismiss } from "../../shared/hooks/useModalDismiss";
import { useCalendar } from "../calendar/store";
import { FALLBACK_HEX } from "../calendar/voyage-mapper";
import { DetailsBody } from "./DetailsBody";
import { isScheduleVoyage, pickModalVoyage } from "./modal-voyage";
import { useVoyageDetails } from "./store";
import s from "./VoyageDetailsModal.module.css";

// Modal popup with the voyage details — opened by double-clicking a
// voyage in either the Port Calendar or the Vessels & Voyages table.
// This is the only details surface in the app; single-click selects
// the voyage (highlight + tooltip enrichment) without opening anything.
export function VoyageDetailsModal() {
  const details = useVoyageDetails();
  const cal = useCalendar();
  const hash = useHashState();

  const detailsView = (): "calendar" | "vessels" | null => {
    const v = hash().view;
    return v === "calendar" || v === "vessels" ? v : null;
  };
  // The popup's voyage: the Prev/Next override takes precedence so
  // popup-internal navigation can hop voyages without disturbing the
  // tab's selection. Falls back to the active tab's selection when no
  // override is set (the usual "double-click → open" case). Resolved
  // via the pure pickModalVoyage helper — single source of truth shared
  // with the test suite.
  const voyage = () =>
    pickModalVoyage(
      details.store.popupVoyageRef,
      details.store.tabSelections,
      detailsView(),
    );

  // The modal owns its currentDetails load. Firing only while the popup
  // is open keeps the store quiet during background tab switches, and
  // makes the load context-aware (the tab the user is actually viewing)
  // without leaking view-awareness into the store. Cache hits resolve
  // instantly — single-click handlers warm the cache eagerly so the
  // typical double-click path opens with no loading flash.
  createEffect(() => {
    if (!details.store.popupOpen) return;
    const v = voyage();
    if (!v) return;
    void details.loadDetailsFor(v.vesselCode, v.voyageNo);
  });

  function close() {
    details.setPopupOpen(false);
  }

  useModalDismiss({ onClose: close, isOpen: () => details.store.popupOpen });

  // Prev/Next voyage ref click — stay in the popup and swap the
  // displayed voyage. No tab switch, no search filter; the underlying
  // tab selection is preserved so closing the popup returns the user
  // to where they were.
  function navigatePopupVoyage(targetVesselCode: string, targetVoyageNo: string) {
    details.navigatePopupVoyage(targetVesselCode, targetVoyageNo);
  }

  const colour = createMemo(() => {
    const v = voyage();
    if (!v) return FALLBACK_HEX;
    if (isScheduleVoyage(v)) return cal.effectivePortColors()[v.portName] ?? FALLBACK_HEX;
    return FALLBACK_HEX;
  });

  return (
    <Show when={details.store.popupOpen && voyage()}>
      {(v) => (
        <Portal>
          <div class={s.backdrop} onMouseDown={close}>
            <div
              class={s.card}
              role="dialog"
              aria-modal="true"
              aria-labelledby="voyage-details-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div class={`${s.header} ${details.isLoadingDetails() ? s.loading : ""}`}>
                <span class={s.color} style={{ background: colour() }} />
                <span id="voyage-details-title">Voyage details</span>
                <button type="button" class={s.close} aria-label="Close" onClick={close}>×</button>
              </div>
              <DetailsBody
                voyage={v()}
                details={details.store.currentDetails}
                currentError={details.store.currentError}
                prevAdjacent={details.prevAdj()}
                nextAdjacent={details.nextAdj()}
                portRefs={cal.state.portRefs}
                portColors={cal.state.portColors}
                onVoyageRefClick={navigatePopupVoyage}
              />
            </div>
          </div>
        </Portal>
      )}
    </Show>
  );
}
